import type {
  ExperiencePayloadEntry,
  SavedExperience,
} from '@/components/experience-repeater';

/**
 * Pet Trainer module-details request/response shapes.
 * Mirrors backend/src/modules/trainer/trainer.validators.ts.
 */

export type TrainerGender = 'MALE' | 'FEMALE' | 'OTHER';

export type TrainingMode = 'TRAINING_CENTER' | 'HOME_VISIT' | 'ONLINE';

export type TrainerServiceRadius = 'KM_5' | 'KM_10' | 'KM_15' | 'KM_20';

export type PetSpecies = 'DOG' | 'CAT' | 'BIRD' | 'RABBIT' | 'FISH' | 'OTHER';

export type TrainerDetailsRequest = {
  full_name: string; // 2..120
  date_of_birth: string; // ISO YYYY-MM-DD, trainer must be 18..100
  gender?: TrainerGender;
  training_mode?: TrainingMode[];
  service_radius: TrainerServiceRadius;
  years_of_experience: number; // 0..80 integer
  trainer_for_species: PetSpecies[]; // 1..6 unique
  /**
   * Prior jobs. Optional — omitting it leaves the stored list alone; sending a list
   * REPLACES it, so the form always submits every row it holds.
   */
  experience?: ExperiencePayloadEntry[];
};

export type TrainerDetailsResponse = {
  id: string;
  vendor_profile_id: string;
  full_name: string;
  date_of_birth: string;
  gender: TrainerGender | null;
  training_mode: TrainingMode[];
  service_radius: TrainerServiceRadius;
  years_of_experience: number;
  trainer_for_species: PetSpecies[];
  created_at: string;
  updated_at: string;
  /** Stored prior jobs for this module, newest first. */
  experience: SavedExperience[];
};

export const TRAINER_GENDER_OPTIONS: Array<{ value: TrainerGender; label: string }> = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
];

export const TRAINING_MODE_OPTIONS: Array<{ value: TrainingMode; label: string }> = [
  { value: 'TRAINING_CENTER', label: 'Training center' },
  { value: 'HOME_VISIT', label: 'Home visit' },
  { value: 'ONLINE', label: 'Online' },
];

export const TRAINER_SERVICE_RADIUS_OPTIONS: Array<{
  value: TrainerServiceRadius;
  label: string;
}> = [
  { value: 'KM_5', label: '5 km' },
  { value: 'KM_10', label: '10 km' },
  { value: 'KM_15', label: '15 km' },
  { value: 'KM_20', label: '20 km' },
];

export const PET_SPECIES_OPTIONS: Array<{ value: PetSpecies; label: string }> = [
  { value: 'DOG', label: 'Dog' },
  { value: 'CAT', label: 'Cat' },
  { value: 'BIRD', label: 'Bird' },
  { value: 'RABBIT', label: 'Rabbit' },
  { value: 'FISH', label: 'Fish' },
  { value: 'OTHER', label: 'Other' },
];
